/**
 * Dashboard Skeleton Component
 * 
 * Loading skeleton for the dashboard page.
 * Matches the layout of the actual dashboard for a smooth transition.
 */

import { Card, CardContent, CardHeader, Skeleton } from '@uprosal/ui';

export function DashboardSkeleton() {
    return (
        <div className="space-y-6">
            {/* Header Skeleton */}
            <div className="flex items-center justify-between">
                <div>
                    <Skeleton className="h-8 w-32 mb-2" />
                    <Skeleton className="h-4 w-48" />
                </div>
            </div>

            {/* KPI Row Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                    <KpiCardSkeleton key={i} />
                ))}
            </div>

            {/* Second Row - Funnel + AI Insights Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <FunnelCardSkeleton />
                <InsightsCardSkeleton />
            </div>

            {/* Third Row - Activity + Quick Actions Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                    <ActivityCardSkeleton />
                </div>
                <QuickActionsCardSkeleton />
            </div>
        </div>
    );
}

function KpiCardSkeleton() {
    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-4 rounded" />
            </CardHeader>
            <CardContent>
                <Skeleton className="h-8 w-20 mb-2" />
                <Skeleton className="h-3 w-24" />
                <div className="mt-3 flex items-end gap-0.5 h-10">
                    {[40, 65, 45, 80, 55, 90, 75].map((_, i) => (
                        <Skeleton key={i} className="flex-1 rounded-t" style={{ height: '60%' }} />
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

function FunnelCardSkeleton() {
    return (
        <Card>
            <CardHeader>
                <Skeleton className="h-6 w-36" />
                <Skeleton className="h-4 w-52 mt-1" />
            </CardHeader>
            <CardContent className="space-y-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-1">
                        <div className="flex justify-between">
                            <Skeleton className="h-4 w-20" />
                            <Skeleton className="h-4 w-8" />
                        </div>
                        <Skeleton className="h-3 w-full rounded-full" />
                    </div>
                ))}
                <Skeleton className="h-3 w-48 mt-2" />
            </CardContent>
        </Card>
    );
}

function InsightsCardSkeleton() {
    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-6 w-24" />
                </div>
                <Skeleton className="h-4 w-64 mt-1" />
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-start gap-3">
                            <Skeleton className="h-4 w-4 mt-0.5 shrink-0" />
                            <Skeleton className="h-4 flex-1" />
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

function ActivityCardSkeleton() {
    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <Skeleton className="h-6 w-32" />
                        <Skeleton className="h-4 w-48 mt-1" />
                    </div>
                    <div className="flex gap-2">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-8 w-14 rounded" />
                        ))}
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex items-center gap-3 p-2">
                            <Skeleton className="h-4 w-4" />
                            <div className="flex-1 min-w-0">
                                <Skeleton className="h-4 w-3/4 mb-1" />
                                <Skeleton className="h-3 w-20" />
                            </div>
                            <Skeleton className="h-5 w-12 rounded-full" />
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

function QuickActionsCardSkeleton() {
    return (
        <Card>
            <CardHeader>
                <Skeleton className="h-6 w-28" />
                <Skeleton className="h-4 w-24 mt-1" />
            </CardHeader>
            <CardContent className="space-y-2">
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full rounded" />
                ))}
            </CardContent>
        </Card>
    );
}
